import Trinity from '@core/scripts/components/trinity-adapter';

// The product-tile click on the two overview pages. Trinity has one schema per page rather than one
// schema with a page discriminator, so the name, the version and the supported `clickName` value all
// travel together in EVENT_BY_PAGE_TYPE below.
//
// Name and version are baked in here instead of being parameters, so a version bump has exactly one
// place to change and no caller can ship a stale version — the same reasoning as the recommendation
// carousel events, see docs/features/trinity-carousel-tracking.md.
export const CLICK_PRODUCT_PLP_NAME = 'CLICK_PRODUCT_PLP';
export const CLICK_PRODUCT_PLP_VERSION = 4;
export const CLICK_PRODUCT_SRP_NAME = 'CLICK_PRODUCT_SRP';
export const CLICK_PRODUCT_SRP_VERSION = 3;

/** The two values `articleOverviewStaticContext.pageType` can take (Constants.LANDMARK_PAGETYPE_*). */
export type OverviewPageType = 'aop' | 'srp';

interface ProductClickEvent {
    name: string;
    version: number;
    /** The only value the schema's `clickName` supports. */
    clickName: string;
    /** The `page` value the other events of this page already send (see components/hooks/use-trinity.js). */
    page: string;
}

// 'aop' is the category/shop page, which Trinity calls the product listing page — hence PLP, not AOP.
const EVENT_BY_PAGE_TYPE: Record<OverviewPageType, ProductClickEvent> = {
    aop: {
        name: CLICK_PRODUCT_PLP_NAME,
        version: CLICK_PRODUCT_PLP_VERSION,
        clickName: 'productCardPlp',
        page: 'aop',
    },
    srp: {
        name: CLICK_PRODUCT_SRP_NAME,
        version: CLICK_PRODUCT_SRP_VERSION,
        clickName: 'productCardSrp',
        page: 'search',
    },
};

/**
 * Where inside the product tile the click happened. A subset of the values the inherited CLICK v41
 * `object` attribute supports: the tile only has the picture and the title/price link.
 */
export type ProductClickObject = 'pic' | 'link';

export interface ProductClickAttributes {
    pageType: OverviewPageType | string;
    /** 1-based position of the product among the products of the *whole* result list, teasers excluded. */
    positionInList: number | undefined;
    /**
     * 1-based position of the product among the products of the *rendered grid*, teasers excluded.
     * In the load-more test group the grid holds every page fetched so far, so this counts across
     * them from the first loaded page — it equals `positionInList` when that page is the first one.
     */
    positionOnPage: number | undefined;
    /** Article number of the colour variant the click navigates to. */
    itemNo: number | undefined;
    /** Digistyle id. Optional in the schema, so it is omitted rather than sent as null. */
    styleId?: number;
    /** Masterstyle id — the same value the page events send as `styleNoList`. Optional in the schema. */
    styleNo?: number;
    object: ProductClickObject;
}

/**
 * Parse a value for one of Trinity's LONG attributes.
 *
 * The overview model types these inconsistently — `gridItem.id` arrives as a number,
 * `masterstyleId` and `colors[].itemNumber` as strings — and an unparsable value must become
 * `undefined` so an optional attribute can be dropped instead of sent as NaN.
 */
export function toLong(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Send the product-tile click event for the page the user is on.
 *
 * Does nothing for an unknown page type, and nothing when one of the schema's mandatory attributes
 * could not be resolved — a dropped event is easier to spot downstream than a malformed one.
 */
export function sendProductClick({
    pageType,
    positionInList,
    positionOnPage,
    itemNo,
    styleId,
    styleNo,
    object,
}: ProductClickAttributes): void {
    const event = EVENT_BY_PAGE_TYPE[pageType as OverviewPageType];
    if (!event) {
        return;
    }
    if (positionInList === undefined || positionOnPage === undefined || itemNo === undefined) {
        return;
    }

    const attributes: Record<string, unknown> = {
        clickName: event.clickName,
        positionInList: positionInList,
        positionOnPage: positionOnPage,
        itemNo: itemNo,
        object: object,
        page: event.page,
    };
    if (styleId !== undefined) {
        attributes.styleId = styleId;
    }
    if (styleNo !== undefined) {
        attributes.styleNo = styleNo;
    }

    Trinity.sendTrinityEvent({
        schema: {name: event.name, version: event.version},
        attributes: attributes,
    });
}
